import { useId, type CSSProperties } from 'react'
import type { WeatherIconKey } from '@/lib/nws/weather-icon'
import './weather-glyph.css'

/**
 * Animated weather glyphs — hand-built line-style SVG in TAW's palette
 * (white-grey structure, cool-blue water, yellow sun). Motion DEPICTS the
 * phenomenon and is purely transform/opacity (compositor-friendly). `urgency`
 * (≈0.7 calm … 1.8 severe) tightens every loop via `--urg`; `calm` is the gentler
 * strip variant. Reduced-motion + off-screen pausing live in the CSS.
 *
 * Layering: the cloud is a transparent line outline, so for "behind the cloud"
 * compositions (partly-cloudy) the sun/moon is masked by the cloud shape — true
 * occlusion with no dependence on the background color. Precip falls from below
 * the cloud's bottom edge.
 */
const SW = 3 // stroke width in the 64×64 viewBox
const CLOUD_PATH = 'M20 43 H43 a7.5 7.5 0 0 0 1-14.9 A10.5 10.5 0 0 0 23.2 25 A7.6 7.6 0 0 0 20 43 Z'

function Cloud({ x = 0, y = 0, className = '' }: { x?: number; y?: number; className?: string }) {
  // Positioning lives on the wrapper <g> so the drift CSS transform on the path
  // composes with it (a CSS transform would otherwise override a transform attr).
  return (
    <g transform={`translate(${x} ${y})`}>
      <path className={`wx-cloud ${className}`} d={CLOUD_PATH} />
    </g>
  )
}

function Sun({ cx = 32, cy = 27, r = 7, full = true }: { cx?: number; cy?: number; r?: number; full?: boolean }) {
  const rays = [0, 45, 90, 135, 180, 225, 270, 315]
  return (
    <g className="wx-sun">
      {full && (
        <g className="wx-sun-rays">
          {rays.map((a) => {
            const rad = (a * Math.PI) / 180
            const x1 = cx + Math.cos(rad) * (r + 3.5)
            const y1 = cy + Math.sin(rad) * (r + 3.5)
            const x2 = cx + Math.cos(rad) * (r + 7)
            const y2 = cy + Math.sin(rad) * (r + 7)
            return <line key={a} x1={x1} y1={y1} x2={x2} y2={y2} />
          })}
        </g>
      )}
      <circle className="wx-sun-core" cx={cx} cy={cy} r={r} />
    </g>
  )
}

function Moon({ small = false }: { small?: boolean }) {
  return small ? (
    <path className="wx-moon" d="M46 24 A8 8 0 1 1 39 16.5 A6.4 6.4 0 1 0 46 24 Z" />
  ) : (
    <path className="wx-moon" d="M40.5 34 A12 12 0 1 1 30 19.5 A9.5 9.5 0 1 0 40.5 34 Z" />
  )
}

/** Place a celestial body BEHIND the cloud: mask it by the (drifting) cloud shape. */
function BehindCloud({
  maskId,
  cx = -2,
  cy = 5,
  children,
}: {
  maskId: string
  cx?: number
  cy?: number
  children: React.ReactNode
}) {
  return (
    <>
      <defs>
        <mask id={maskId} maskUnits="userSpaceOnUse">
          <rect x="0" y="0" width="64" height="64" fill="#fff" />
          <g transform={`translate(${cx} ${cy})`}>
            <path
              className="wx-cloud--drift"
              d={CLOUD_PATH}
              fill="#000"
              stroke="#000"
              strokeWidth="3.5"
              strokeLinejoin="round"
            />
          </g>
        </mask>
      </defs>
      <g mask={`url(#${maskId})`}>{children}</g>
      <Cloud x={cx} y={cy} className="wx-cloud--drift" />
    </>
  )
}

function Rain({ drops = [26, 32, 38] }: { drops?: number[] }) {
  // Emerge from below the cloud's bottom edge (y≈43); never inside the cloud body.
  return (
    <g className="wx-water">
      {drops.map((x, i) => (
        <line key={x} className="wx-drop" style={{ animationDelay: `${i * -0.42}s` }} x1={x} y1={52} x2={x - 2.5} y2={58} />
      ))}
    </g>
  )
}

function Drizzle() {
  return (
    <g className="wx-water">
      {[27, 37].map((x, i) => (
        <line
          key={x}
          className="wx-drop wx-drop--slow"
          style={{ animationDelay: `${i * -0.6}s` }}
          x1={x}
          y1={53}
          x2={x - 1.5}
          y2={57}
        />
      ))}
    </g>
  )
}

function Bolt() {
  return <path className="wx-bolt" d="M34 44 L28 53 H33 L31 60 L40 49 H35 L38 44 Z" />
}

function Snow() {
  return (
    <g className="wx-snow">
      <g className="wx-flake-spin">
        <line x1={32} y1={47} x2={32} y2={57} />
        <line x1={27} y1={49.5} x2={37} y2={54.5} />
        <line x1={37} y1={49.5} x2={27} y2={54.5} />
      </g>
      <circle className="wx-drift" style={{ animationDelay: '-0.4s' }} cx={24} cy={49} r={1.4} />
      <circle className="wx-drift" style={{ animationDelay: '-1.6s' }} cx={41} cy={50} r={1.4} />
    </g>
  )
}

function Hail() {
  return (
    <g className="wx-water">
      {[27, 37].map((x, i) => (
        <circle key={x} className="wx-drop" style={{ animationDelay: `${i * -0.5}s` }} cx={x} cy={53} r={1.7} />
      ))}
    </g>
  )
}

function Wind() {
  return (
    <g className="wx-wind" style={{ color: 'var(--text-2)' }}>
      <path className="wx-gust" style={{ animationDelay: '0s' }} d="M14 26 h22 a4 4 0 1 0 -4 -4" />
      <path className="wx-gust" style={{ animationDelay: '-1.1s' }} d="M16 34 h28 a4.5 4.5 0 1 1 -4.5 4.5" />
      <path className="wx-gust" style={{ animationDelay: '-0.55s' }} d="M14 42 h18 a3.5 3.5 0 1 0 -3.5 3.5" />
    </g>
  )
}

function Fog() {
  return (
    <g className="wx-fog" style={{ color: 'var(--text-2)' }}>
      {[44, 50, 56].map((y, i) => (
        <line key={y} className="wx-fog-line" style={{ animationDelay: `${i * -0.8}s` }} x1={18} y1={y} x2={46} y2={y} />
      ))}
    </g>
  )
}

function Funnel() {
  return (
    <g className="wx-funnel">
      <path d="M16 18 H48" />
      <path d="M20 26 H44" />
      <path d="M24 34 H40" />
      <path d="M29 42 H35" />
      <path d="M32 42 L33 50" />
    </g>
  )
}

function render(code: WeatherIconKey, uid: string) {
  switch (code) {
    case 'clear-day':
      return <Sun cx={32} cy={30} r={8} />
    case 'clear-night':
      return <Moon />
    case 'partly-day':
      return (
        <BehindCloud maskId={`wxm-${uid}-pd`}>
          <Sun cx={43} cy={21} r={7} />
        </BehindCloud>
      )
    case 'partly-night':
      return (
        <BehindCloud maskId={`wxm-${uid}-pn`}>
          <Moon small />
        </BehindCloud>
      )
    case 'cloudy':
      return <Cloud className="wx-cloud--drift" />
    case 'rain':
      return (
        <>
          <Cloud />
          <Rain />
        </>
      )
    case 'drizzle':
      return (
        <>
          <Cloud />
          <Drizzle />
        </>
      )
    case 'thunderstorm':
      return (
        <>
          <Cloud />
          <Rain drops={[25, 41]} />
          <Bolt />
        </>
      )
    case 'snow':
      return (
        <>
          <Cloud />
          <Snow />
        </>
      )
    case 'hail':
      return (
        <>
          <Cloud />
          <Hail />
        </>
      )
    case 'fog':
      return (
        <>
          <Cloud x={0} y={-4} />
          <Fog />
        </>
      )
    case 'wind':
      return <Wind />
    case 'tornado':
      return <Funnel />
    default:
      return <Cloud className="wx-cloud--drift" />
  }
}

export function WeatherGlyph({
  code,
  size = 24,
  className,
  calm = false,
  urgency,
}: {
  code: WeatherIconKey
  size?: number
  className?: string
  /** Gentler/slower variant for the hourly + 7-day strips. */
  calm?: boolean
  /** Motion urgency (~0.7 calm … 1.8 severe). Overrides `calm` when set. */
  urgency?: number
  /** Accepted for call-site compatibility (no stroke-width variance). */
  strokeWidth?: number
}) {
  const urg = urgency ?? (calm ? 0.72 : 1)
  const uid = useId().replace(/:/g, '')
  return (
    <svg
      className={`wx ${calm ? 'wx--calm' : ''} ${className ?? ''}`}
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      stroke="currentColor"
      strokeWidth={SW}
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-hidden="true"
      style={{ ['--urg' as keyof CSSProperties]: urg } as CSSProperties}
    >
      {render(code, uid)}
    </svg>
  )
}
